import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Web Speech API wrapper for reading Bible passages aloud.
 * State: 'idle' | 'playing' | 'paused'
 */
export default function useSpeech() {
  const [state, setState] = useState("idle");
  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURI] = useState(() => localStorage.getItem("bible_voice") || "");
  const [rate, setRate] = useState(() => parseFloat(localStorage.getItem("bible_rate") || "0.95"));
  const utterRef = useRef(null);
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;

  useEffect(() => {
    if (!supported) return;
    const load = () => {
      const v = window.speechSynthesis.getVoices();
      setVoices(v);
      if (!voiceURI && v.length) {
        // Prefer an English voice
        const en = v.find((x) => /en(-|_)?/i.test(x.lang)) || v[0];
        setVoiceURI(en.voiceURI);
      }
    };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported]);

  useEffect(() => { if (voiceURI) localStorage.setItem("bible_voice", voiceURI); }, [voiceURI]);
  useEffect(() => { localStorage.setItem("bible_rate", String(rate)); }, [rate]);

  const stop = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setState("idle");
  }, [supported]);

  const play = useCallback((text) => {
    if (!supported || !text) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const v = voices.find((x) => x.voiceURI === voiceURI);
    if (v) u.voice = v;
    u.rate = rate;
    u.pitch = 1;
    u.onend = () => setState("idle");
    u.onerror = () => setState("idle");
    utterRef.current = u;
    window.speechSynthesis.speak(u);
    setState("playing");
  }, [supported, voices, voiceURI, rate]);

  const pause = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.pause();
    setState("paused");
  }, [supported]);

  const resume = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.resume();
    setState("playing");
  }, [supported]);

  // Cleanup on unmount
  useEffect(() => () => { if (supported) window.speechSynthesis.cancel(); }, [supported]);

  return { supported, state, voices, voiceURI, setVoiceURI, rate, setRate, play, pause, resume, stop };
}
